import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

/* Services */
import { Api } from './API.service';
import { getAuthToken } from './Auth.service';
import { ApiRoute } from './../config/Base.config';
import { encodeParams, removeDuplicateValue } from 'helpers/common.helper';

/* Interfaces */

/* configs */
import { apiQshipURL } from '../config/App.config';

export class QshipApi extends Api {
  public constructor() {
    super();

    // // this middleware is been called right before the http request is made.
    this.instance.interceptors.request.use((param: AxiosRequestConfig) => ({
      ...param,
    }));

    // // this middleware is been called right before the response is get it by the method that triggers the request
    this.instance.interceptors.response.use((param: AxiosResponse) => ({
      ...param
    }));
    this.setToken(getAuthToken());
  }

  public async getDashboard(apiUrl: string, hasSegment?: boolean): Promise<any> {
    let url = `${apiQshipURL}${apiUrl}?page=1&per_page=5`;
    if (hasSegment !== undefined) url += `&segment=${hasSegment}`;
    try {
      return await this.get<any,
        AxiosResponse<any>>(url).
        then(this.success).catch((error: AxiosError<Error>) => {
          throw error;
        });
    } catch (error) {
      throw error;
    }
  }

  public async getTracking(apiUrl, page: number, perPage: number, obj?: any, searchFilter?: string, dateFilter?: any, sortBy?: string, isHasSegment?: boolean): Promise<any> {
    let params = [];
    let queryString = '';

    if(!apiUrl) return

    if(obj){
      let statusFilter = Object.keys(obj);
      if(statusFilter.includes("tracking_ids")){
        const ids = obj["tracking_ids"].map(item => encodeURIComponent(item));
        params.push("tracking_ids[]=" + ids.join("&tracking_ids[]="));
      }
      statusFilter.forEach(function (iter) {
        if(!iter.includes("tracking_ids"))
          for (let country of obj[iter]) {
            if(['on_time','delayed','on_hold'].includes(country))
              params.push('shipment_status[]=' + country);
            else
              params.push(iter + '[]=' + country);
        }
      });
    }

    if (searchFilter) {
      let trackingIds = searchFilter
        .split(",")
        .join(";")
        .split(";")
        .filter(function (el) {
          return el;
        });
      if (trackingIds && trackingIds.length) {
        trackingIds = trackingIds.map((el) => el.trim());
        const uniqueIdsParam = removeDuplicateValue(trackingIds);
        let idsParam = encodeParams(uniqueIdsParam).join("&query_params[]=");
        idsParam = `query_params[]=${idsParam}`;
        params.push(idsParam);
      }
    }

    if (apiUrl === ApiRoute.tracking_outbound || apiUrl === ApiRoute.tracking_inbound) {
      if (dateFilter && dateFilter.fromDate && dateFilter.toDate) {
        params.push(`from_date=${dateFilter.fromDate}`);
        params.push(`to_date=${dateFilter.toDate}`);
      }
    } else {
      if (dateFilter && dateFilter.fromDate && dateFilter.toDate) {
        params.push(`start_complete_date=${dateFilter.fromDate}`);
        params.push(`end_complete_date=${dateFilter.toDate}`);
      }
    }

    if (sortBy) {
      params.push(`sort_track_id=${sortBy}`)
    }

    if (isHasSegment !== undefined) {
      params.push(`segment=${isHasSegment}`);
    }

    if (params.length > 0) {
        queryString = params.join("&");
    }
    try {
        return await this.get<any,
            AxiosResponse<any>>(`${apiQshipURL}${apiUrl}?page=${page}&per_page=${perPage}${queryString ? '&' + queryString : ''}`).
            then(this.success).catch((error: AxiosError<Error>) => {
                throw error;
            });
    } catch (error) {
        throw error;
    }
  }

  public async exportShipmentList(apiUrl, obj: any, searchFilter?: string, dateFilter?: any, isHasSegment?: boolean): Promise<any> {
    let params = [];
    let queryString = '';
    let keys = Object.keys(obj);

    keys.forEach(function (iter) {
      for (let country of obj[iter]) {
        if(['on_time','delayed','on_hold'].includes(country))
          params.push('shipment_status[]=' + country);
        else
          params.push(iter + '[]=' + country);
      }
    });

    if (searchFilter) {
      let trackingIds = searchFilter
        .split(",")
        .join(";")
        .split(";")
        .filter(function (el) {
          return el;
        });
      if (trackingIds && trackingIds.length) {
        trackingIds = trackingIds.map((el) => el.trim());
        let idsParam = [...new Set(trackingIds)].join("&query_params[]=");
        idsParam = `query_params[]=${idsParam}`;
        params.push(idsParam);
      }
    }

    if (apiUrl === ApiRoute.tracking_outbound || apiUrl === ApiRoute.tracking_inbound) {
      if (dateFilter && dateFilter.fromDate && dateFilter.toDate) {
        params.push(`from_date=${dateFilter.fromDate}`);
        params.push(`to_date=${dateFilter.toDate}`);
      }
    } else {
      if (dateFilter && dateFilter.fromDate && dateFilter.toDate) {
        params.push(`start_complete_date=${dateFilter.fromDate}`);
        params.push(`end_complete_date=${dateFilter.toDate}`);
      }
    }

    if (isHasSegment !== undefined) {
      params.push(`segment=${isHasSegment}`);
    }

    if (params.length > 0) {
      queryString = params.join("&");
    }

    try {
      return await this.get<any,
        AxiosResponse<any>>(`${apiQshipURL}${apiUrl}.csv${queryString ? '?' + queryString : ''}`).
        then(this.success).catch((error: AxiosError<Error>) => {
          throw error;
        });
    } catch (error) {
        throw error;
    }
  }

  public downloadExportedFile(id: string): Promise<any> {
    const url = `${apiQshipURL}${ApiRoute.downloadExportedFile}`.replace("{id}", id);
    return this.get<any, AxiosResponse<any>>(url, { responseType: "blob" }).then(
      this.success
    );
  }

  public getExportingFileStatus(id: string): Promise<any> {
    const url = `${apiQshipURL}${ApiRoute.getExportingFileStatus}`.replace("{id}", id);
    return this.get<any, AxiosResponse<any>>(url).then(
      this.success
    );
  }

  public sendExportedFileViaMail(id: string): Promise<any> {
    const url = `${apiQshipURL}${ApiRoute.sendExportedFileViaMail}`.replace("{id}", id);
    return this.put<any, AxiosResponse<any>>(url).then(
      this.success
    );
  }

  public async verifyTrackingIds(payload: any, isSecure?: boolean): Promise<any> {
    try {
      let url = `${apiQshipURL}${
        isSecure ? ApiRoute.verify_secure_trackingIds : ApiRoute.verify_non_login_trackingIds
      }?${payload}`;
      return await this.getWithNonToken<any, AxiosResponse<any>>(url).
        then(this.success).catch((error: AxiosError<Error>) => {
          throw error;
        });
    } catch (error) {
      throw error;
    }
  }

  public async getGPSMapDetail(orderId: string, isLoggedIn: boolean, jobType: string): Promise<any> {
    let url = `${apiQshipURL}${ApiRoute.gps_map_detail_non_login.replace(":orderId", orderId).replace(":jobType", jobType)}`;
    if (isLoggedIn) url = `${apiQshipURL}${ApiRoute.gps_map_detail.replace(":orderId", orderId).replace(":jobType", jobType)}`;
    try {
      return await this.get<any, AxiosResponse<any>>(url)
        .then(this.success)
        .catch((error: AxiosError<Error>) => {
          throw error;
        });
    } catch (error) {
      throw error;
    }
  }

  public async getEPOPNonLogin(orderId: string): Promise<any> {
    const url = `${apiQshipURL}${ApiRoute.getEPOPNonLogin}?order_id=${orderId}`;
    try {
      return await this.get<any, AxiosResponse<any>>(url)
        .then(this.success)
        .catch((error: AxiosError<Error>) => {
          throw error;
        });
    } catch (error) {
      throw error;
    }
  }

  public async getOrderSegments(orderId: string, isLoggedIn: boolean): Promise<any> {
    const url = `${apiQshipURL}${isLoggedIn ? ApiRoute.getOrderSegments : ApiRoute.getOrderSegmentsNonLogin}`.replace(
      "{orderId}",
      orderId
    );
    try {
      return await this.get<any, AxiosResponse<any>>(url)
        .then(this.success)
        .catch((error: AxiosError<Error>) => {
          throw error;
        });
    } catch (error) {
      throw error;
    }
  }

  public async getGPSTracking(segmentId: string, jobType?: string): Promise<any> {
    let url = `${apiQshipURL}${ApiRoute.getGPSTracking}`.replace("{segmentId}", segmentId);
    if (jobType) url += `&job_type=${jobType}`;
    try {
      return await this.get<any, AxiosResponse<any>>(url)
        .then(this.success)
        .catch((error: AxiosError<Error>) => {
          throw error;
        });
    } catch (error) {
      throw error;
    }
  }

  public async getOrderActivities(orderId: string, isSecure: boolean, segmentId?: string): Promise<any> {
    const endpoint = isSecure ? ApiRoute.getOrderActivities : ApiRoute.getOrderActivitiesUnauthenticated;
    let url = `${apiQshipURL}${endpoint}`.replace("{orderId}", orderId);
    if (segmentId) url += `?segment_id=${segmentId}`;
    try {
      return await this.get<any, AxiosResponse<any>>(url)
        .then(this.success)
        .catch((error: AxiosError<Error>) => {
          throw error;
        });
    } catch (error) {
      throw error;
    }
  }
}